import { AudioChunk } from '../types/audio';

/**
 * Audio encoding service for WebSocket transmission
 * Handles encoding of audio chunks for network transmission
 * Requirements: 4.2
 */
export class AudioEncoder {
  /**
   * Encode audio chunk for WebSocket transmission
   * Converts ArrayBuffer to base64 for JSON serialization
   */
  static encodeAudioChunk(chunk: AudioChunk): AudioChunk {
    try {
      // Convert ArrayBuffer to base64 string for JSON transmission
      const base64Data = AudioEncoder.arrayBufferToBase64(chunk.data);
      
      return {
        ...chunk,
        data: base64Data as any, // Will be serialized as string in JSON
      };
    } catch (error) {
      console.error('Failed to encode audio chunk:', error);
      throw new Error('Audio encoding failed');
    }
  }

  /**
   * Decode audio chunk received from WebSocket
   * Converts base64 string back to ArrayBuffer
   */
  static decodeAudioChunk(encodedChunk: any): AudioChunk {
    try {
      // Convert base64 string back to ArrayBuffer
      const arrayBuffer = AudioEncoder.base64ToArrayBuffer(encodedChunk.data);
      
      return {
        ...encodedChunk,
        data: arrayBuffer,
      };
    } catch (error) {
      console.error('Failed to decode audio chunk:', error);
      throw new Error('Audio decoding failed');
    }
  }

  /**
   * Convert ArrayBuffer to base64 string
   */
  private static arrayBufferToBase64(buffer: ArrayBuffer): string {
    const bytes = new Uint8Array(buffer);
    let binary = '';
    
    for (let i = 0; i < bytes.byteLength; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    
    return btoa(binary);
  }

  /**
   * Convert base64 string to ArrayBuffer
   */
  private static base64ToArrayBuffer(base64: string): ArrayBuffer {
    const binaryString = atob(base64);
    const bytes = new Uint8Array(binaryString.length);
    
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    
    return bytes.buffer;
  }

  /**
   * Validate audio chunk before encoding
   */
  static validateAudioChunk(chunk: AudioChunk): { valid: boolean; error?: string } {
    if (!chunk) {
      return { valid: false, error: 'Audio chunk is null or undefined' };
    }

    if (!chunk.data || !(chunk.data instanceof ArrayBuffer)) {
      return { valid: false, error: 'Audio chunk data must be an ArrayBuffer' };
    }

    if (chunk.data.byteLength === 0) {
      return { valid: false, error: 'Audio chunk data is empty' };
    }

    if (typeof chunk.timestamp !== 'number' || chunk.timestamp <= 0) {
      return { valid: false, error: 'Audio chunk timestamp must be a positive number' };
    }

    if (typeof chunk.sequenceNumber !== 'number' || chunk.sequenceNumber < 0) {
      return { valid: false, error: 'Audio chunk sequence number must be a non-negative number' };
    }

    // Check for reasonable audio chunk size (between 1KB and 1MB)
    const minSize = 1024; // 1KB
    const maxSize = 1024 * 1024; // 1MB
    if (chunk.data.byteLength < minSize || chunk.data.byteLength > maxSize) {
      return { 
        valid: false, 
        error: `Audio chunk size ${chunk.data.byteLength} bytes is outside acceptable range (${minSize}-${maxSize} bytes)` 
      };
    }

    return { valid: true };
  }

  /**
   * Get audio chunk size in bytes
   */
  static getChunkSize(chunk: AudioChunk): number {
    return chunk.data instanceof ArrayBuffer ? chunk.data.byteLength : 0;
  }

  /**
   * Calculate estimated transmission size after base64 encoding
   */
  static getEncodedSize(chunk: AudioChunk): number {
    const originalSize = AudioEncoder.getChunkSize(chunk);
    // Base64 encoding increases size by ~33%
    return Math.ceil(originalSize * 4 / 3);
  }
}